// biome-ignore-all lint/performance/noJsxPropsBind: pre-existing, outside the ADS color pairing in ENG-1520
import classNames from 'classnames';
import { memo, useState } from 'react';

import { type Color, ColorPicker, type ColorPickerProps } from '@air/primitive-color-picker';
import { Popover, type PopoverProps } from '@air/primitive-popover';
import { VisuallyHidden } from '@air/primitive-visually-hidden';

export type ColorPickerMenuProps = Pick<PopoverProps, 'align' | 'className' | 'open'> &
  Pick<ColorPickerProps, 'colors'> & {
    selectedColor?: Color;
    onSelect?: (color: Color) => void;
  };

export const ColorPickerMenu = memo(
  ({ align = 'end', className, colors, onSelect, open, selectedColor, ...restOfProps }: ColorPickerMenuProps) => {
    const [isOpened, setIsOpened] = useState(open);

    return (
      <Popover
        align={align}
        className={classNames('min-w-0', className)}
        onOpenChange={(open) => setIsOpened(open)}
        open={isOpened}
        sideOffset={8}
        trigger={
          <button
            className="flex size-8 items-center justify-center rounded-4 border ads:border-default border-gray-4 ads:bg-base bg-gray-1"
            type="button"
          >
            <div
              className={classNames(
                'h-4 w-4 rounded-2 ads:bg-subtle bg-gray-3',
                selectedColor?.hex === '#FFFFFF' ? 'border ads:border-default border-gray-4' : 'border-none',
              )}
              style={{ backgroundColor: selectedColor?.hex }}
            />
            <VisuallyHidden>{selectedColor?.name || 'Unselected'}</VisuallyHidden>
          </button>
        }
        {...restOfProps}
      >
        <ColorPicker
          colors={colors}
          onSelect={(color) => {
            setIsOpened(false);
            onSelect?.(color);
          }}
        />
      </Popover>
    );
  },
);

ColorPickerMenu.displayName = 'ColorPickerMenu';
